'use client';

import React, { useEffect, useState } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import type {
  CategoriesEntity,
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Logo } from '../ui/logo';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { SearchButton } from './search-button';
import { HeaderProfileButton } from './header-profile-button';
import { HeaderFavoritesButton } from './header-favorites-button';
import { HeaderCartButton } from './header-cart-button';
import MobileSidebar from './mobile-sidebar';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  categories: CategoriesEntity[];
  menu: Menu;
};

export function HeaderMain({ generalLayout, categories, menu }: HeaderMainProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const SCROLL_DOWN_THRESHOLD = 64;
    const SCROLL_UP_THRESHOLD = 40;

    const handleScroll = () => {
      const scrollY = window.scrollY;

      setScrolled(prev => {
        // Only change state when crossing thresholds
        if (!prev && scrollY > SCROLL_DOWN_THRESHOLD) {
          return true;
        }
        if (prev && scrollY < SCROLL_UP_THRESHOLD) {
          return false;
        }
        return prev;
      });
    };

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <MobileSearchProvider>
      <div
        className={cn(
          'custom-section',
          'hidden bg-white md:flex',
          'ea-header-main__primary-section',
          scrolled ? 'ea-header-main__scrolled py-4 lg:py-[26px]' : 'py-6 lg:py-8',
        )}
      >
        <div className={cn('custom-container--xxl', 'flex items-center justify-between gap-6')}>
          <div className="flex items-center justify-start gap-16 xl:gap-24">
            <Logo
              className={{
                image: cn(
                  scrolled ? 'h-8 lg:h-10 xl:h-12' : 'h-12 lg:h-16 xl:h-[68px]',
                  'transition-all duration-300 ease-in-out',
                ),
              }}
              generalLayout={generalLayout}
            />
            <div className="flex items-center justify-start gap-6">
              {categories.map(category => (
                <Link prefetchOnHover
                  prefetch={false}
                  key={category.id}
                  href={ROUTES.CATEGORY({ slug: category.slug! })}
                  className="font-medium tracking-tight transition-all hover:opacity-80"
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 xl:gap-3">
            <SearchButton />
            <HeaderProfileButton />
            <HeaderFavoritesButton />
            <HeaderCartButton />
          </div>
        </div>
      </div>
      <section
        className={cn(
          'custom-section',
          'flex bg-white py-4 md:hidden',
          scrolled && 'ea-header-main__scrolled',
        )}
      >
        <div className={cn('custom-container', 'flex items-center justify-between gap-4')}>
          <MobileSidebar side="left" menu={menu.fields} />
          <Logo generalLayout={generalLayout} className={{ image: 'max-h-8 min-h-8' }} />
          <SearchButton variant={'secondary'} />
        </div>
      </section>
    </MobileSearchProvider>
  );
}
